'use client';

import type { components } from '@/apis/spec/contentType';
import Icon from '@/Icon';
import Img from '@/legacy/components/img/FillImage';
import Nation from '@/legacy/components/info/Nation';
import { TOURNAMENT_DETAILS_PATHNAME } from '@/legacy/constants/pathname';
import useLocaleLink from '@/legacy/hooks/useLocaleLink';
import { nullCashReplace, nullCasualReplace } from '@/utilities/stateTool';
import Link from 'next/link';
import { useState } from 'react';
import { EffectCoverflow, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css/navigation';

import { clc } from '@/legacy/components/base/utils';
import Crown from '@/legacy/components/new/Crown';
import Private from '@/legacy/components/new/Private';
import { localeWebToStepin } from '@/legacy/components/new/tournament';
import { Status } from '@/legacy/components/tournament/Status';

// Import Swiper styles
import styles from '@/legacy/components/new/tournamentMain.module.scss';
import 'swiper/css';

// Import domains API
import { useFetchV2TournamentMainQuery } from '@/domains/content/api/v2_tournament/useV2_tournamentQueries';

const Page = ({ item, page }: { page: number } & { item: components['schemas']['TournamentDetailResponseDto'] }) => {
	const isDiamond = item.tournament.tournamentType === 'CASUAL';
	const isNation = item.tournament.nationFlagImageUrl != null;
	const locale = useLocaleLink();

	const isFill = item.tournament.posterImageUrl != null;

	return (
		<Link className={styles.posterFrame} href={TOURNAMENT_DETAILS_PATHNAME(locale) + '?id=' + item.tournament.id}>
			{isDiamond ? (
				<Img
					className={clc(isFill && styles.fixed, styles.poster)}
					src={nullCasualReplace(item.tournament.posterImageUrl)}
				/>
			) : (
				<Img
					className={clc(isFill && styles.fixed, styles.poster)}
					src={nullCashReplace(item.tournament.posterImageUrl)}
				/>
			)}
			<div className={styles.mainInfo}>
				<div className={styles.infoTop}>
					<div className={styles.private}>{item.tournament.isPrivate && <Private className={styles.private} />}</div>
					{!isDiamond && <Crown className={styles.crown} />}

					<Nation
						className={styles.nation}
						nationCode={item.tournament.nationCode}
						nationFlagImageUrl={item.tournament.nationFlagImageUrl}
					/>
				</div>
				<div className={styles.infoBottom}>
					<div className={styles.info}>
						<Status className={styles.badge} status={item.tournament.status} />
						<span className={styles.title}>{item.tournament.title}</span>
					</div>
					{/* <Party count={page} capacity={pageLimit}></Party> */}
					{/* <Party count={item.tournament.participantCount} capacity={item.tournament.capacity}></Party> */}
				</div>
			</div>

			<div className="before" />
			<div className="after" />
			<div className={styles.bottom} />
		</Link>
	);
};

const pageLimit = 4;
const TournamentMain = () => {
	const locale = useLocaleLink();
	const [step, setStep] = useState(1);

	const { data, error, isLoading } = useFetchV2TournamentMainQuery({
		query: {
			hl: localeWebToStepin[locale],
			limit: pageLimit,
			page: 1,
		},
	});

	const tournaments = data?.data || [];

	// 에러 상태 처리
	if (error) {
		return <div className={styles.heroSection} />;
	}

	if (!isLoading && Array.isArray(tournaments) && tournaments.length > 0)
		return (
			<div className={styles.heroSection}>
				<Swiper
					centeredSlides
					className="mainSwiper"
					coverflowEffect={{
						depth: 0,
						modifier: 1,
						rotate: 0,
						scale: 0.8,
						slideShadows: true,
						stretch: 500,
					}}
					effect={'coverflow'}
					grabCursor={true}
					modules={[EffectCoverflow, Navigation]}
					navigation={{
						disabledClass: 'main-button-disabled',
						nextEl: '.main-button-next',
						prevEl: '.main-button-prev',
					}}
					onSlideChange={(e) => {
						setStep(e.realIndex + 1);
					}}
					slidesPerView={'auto'}
				>
					{tournaments.map((item, index) => {
						return (
							<SwiperSlide key={index}>
								<Page item={item} page={step} />
							</SwiperSlide>
						);
					})}
				</Swiper>
				<div className="main-button-prev">
					<Icon path="Left" />
				</div>
				<div className="main-button-next">
					<Icon path="Right" />
				</div>
			</div>
		);

	return <div className={styles.heroSection} />;
};

export default TournamentMain;
